import { useState, useCallback, useMemo } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import {
  Col,
  Container,
  Row,
} from 'shared-portal-component';

import ActivityFeed from '@/components/organism/SixDetail/ActivityFeed/ActivityFeed';
import SixHeader from '@/components/organism/SixDetail/Header/Header';
import SixTable from '@/components/organism/SixDetail/SixTable/SixTable';
import ConnectionStatusDot from '@/components/atomic/ConnectionStatusDot/ConnectionStatusDot';
import CreateFolderModal from '@/components/molecules/CreateFolderModal/CreateFolderModal';
import { useSixDetail, useSixFileUpload } from '@/hooks/six';
import { useAppDispatch } from '@/store';
import { clearUploadProgress, setUploadProgress } from '@/store/slices/poc/heliaSlice';
import { retryFileAnnouncement } from '@/store/thunks/poc/helia';
import FolderToolbar from '@/components/organism/FolderToolbar/FolderToolbar';

const MAX_RETRY_COUNT = 3;

interface SixDetailPageContentProps {
  sixId: string;
  initialPath?: string;
}

/**
 * Inner content component that accepts sixId as a prop.
 * This allows reuse from different route wrappers (e.g., dev route).
 */
export function SixDetailPageContent({ sixId, initialPath }: SixDetailPageContentProps) {
  const { state, handlers } = useSixDetail(sixId, initialPath);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {
    isUploading,

    uploadProgress,
    currentPath,
    fileInputRef,
    handleUploadFile,
    handleFileSelect,
    createNewFolder
  } = useSixFileUpload(sixId);

  const handleProgressComplete = useCallback((fileName: string) => {

    dispatch(clearUploadProgress(fileName));
  }, [dispatch]);

  const handleRetry = useCallback((fileName: string) => {
    if (!(fileName in uploadProgress)) {
      return;
    }
    const entry = uploadProgress[fileName];

    const currentRetryCount = entry.retryCount ?? 0;
    if (currentRetryCount >= MAX_RETRY_COUNT) {
      dispatch(setUploadProgress({
        fileName,
        stage: 'error',
        progress: 0,
        error: 'Upload failed after multiple attempts. Please try again later.',
        retryCount: currentRetryCount,
      }));
      return;
    }

    if (entry.cid == null || entry.sixId == null || entry.metadata == null) {
      return;
    }

    // Thunk checks CID validity before re-announcing.
    // If the DAG is corrupt, it sets an error instead of retrying.
    void dispatch(retryFileAnnouncement({
      fileName,
      cid: entry.cid,
      sixId: entry.sixId,
      metadata: entry.metadata,
      retryCount: currentRetryCount + 1,
    }));
  }, [dispatch, uploadProgress]);

  const handleOpenFolderModal = useCallback(() => {
    setFolderModalOpen(true);
  }, []);

  const handleCloseFolderModal = useCallback(() => {
    setFolderModalOpen(false);
  }, []);

  const handleCreateFolder = useCallback((folderName: string) => {
    void createNewFolder(folderName)
      .then(() => {
        setFolderModalOpen(false);
      })
      .catch(() => {
        // Error already logged in hook, keep modal open for retry
      });
  }, [createNewFolder]);

  // Extract existing folder names at current level for duplicate validation
  const existingFolderNames = useMemo(
    () => state.files
      .filter(file => file.type === 'folder')
      .map(folder => folder.name),
    [state.files]
  );

  return (
    <Container fluid>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        style={{ display: 'none' }}
        accept="*/*"
      />

      {/* Create Folder Modal */}
      <CreateFolderModal
        open={folderModalOpen}
        onClose={handleCloseFolderModal}
        onConfirm={handleCreateFolder}
        currentPath={currentPath}
        existingFolderNames={existingFolderNames}
      />
      <Row spacing="none" justify="start">
        <Col size={4}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
            <ConnectionStatusDot

              isConnected={state.connectionStatus.isConnected}

              relayCount={state.connectionStatus.relayCount}

              peerCount={state.connectionStatus.peerCount}
            />
            <span style={{ fontSize: '0.875rem', color: '#666' }}>
              { }
              {state.connectionStatus.isConnected ? 'Connected' : 'Connecting...'}
            </span>
          </div>
          <SixHeader
            orgName={state.orgName}
            filesCount={state.filesCount}
            onBack={handlers.navigateBack}
          />
          <ActivityFeed activities={state.activities} participants={state.participants} />
        </Col>

        <Col size={8}>
          {/* Search bar */}
          {/* <div className="mb-4">
            <SearchInput
              placeholder="Search in a SIX"
              onSearch={handlers.handleSearch}
              onMenuClick={handlers.handleMenu}
              className="w-full max-w-xl"
            />
          </div> */}

          {/* Integrated folder toolbar with navigation and actions */}
          <FolderToolbar
            currentPath={currentPath}
            isAtRoot={state.isAtRoot}
            isUploading={isUploading}
            onNavigate={handlers.navigateToFolder}
            onNavigateUp={handlers.navigateUp}
            onNewFolder={handleOpenFolderModal}
            onUpload={handleUploadFile}
          />

          <div style={{ marginTop: '0.75rem' }}>
            <SixTable
              data={state.files}
              isAtRoot={state.isAtRoot}
              onFolderClick={handlers.handleFolderClick}
              onNavigateUp={handlers.navigateUp}
              handleDownload={handlers.handleDownload}
              handleDeleteFile={handlers.handleDeleteFile}
              handleDeleteFolder={handlers.handleDeleteFolder}
              canDeleteFile={state.canDeleteFile}
              canDeleteFolder={state.canDeleteFolder}
              uploadProgress={uploadProgress}
              onProgressComplete={handleProgressComplete}
              onRetry={handleRetry}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );
}

/**
 * Route-aware wrapper that extracts sixId from /app/sixes/$sixId route.
 */
export function SixDetailPage() {
  const { sixId } = useParams({ from: '/app/sixes/$sixId' });
  const search: { path?: string } = useSearch({ strict: false });
  return <SixDetailPageContent sixId={sixId} initialPath={search.path} />;
}

export default SixDetailPage;
